<template>
  <div>
    <div class="row">
      <div class="col-lg-8 mx-auto">
        <div
          v-if="productInfo && productInfo.title"
          class="text-dark font-size-24 font-weight-bold mb-4"
        >
          <span>
            {{ $l(productInfo.title) }}
          </span>
          <a-button
            type="link"
            icon="edit"
            @click="openTitleModal"
          />
        </div>
        <div class="card">
          <div class="card-body">
            <div class="table-responsive">
              <table
                v-if="productInfo"
                class="table table-borderless"
              >
                <tbody>
                  <tr>
                    <td class="text-gray-6 pl-0">
                      {{ $t('product.categories.single') }} :
                    </td>
                    <td class="pr-0 text-dark">
                      <template v-if="productInfo.categories && productInfo.sub_categories && productInfo.sections">
                        <div
                          v-for="(category, index) in productInfo.categories"
                          :key="index"
                        >
                          <span>{{ $l(category) + ' / ' }}</span>
                          <span v-if="productInfo.sub_categories && productInfo.sub_categories[index]">
                            {{ $l(productInfo.sub_categories[index]) + ' / ' }}
                          </span>
                          <span v-if="productInfo.sections && productInfo.sections[index]">
                            {{ $l(productInfo.sections[index]) }}
                          </span>
                          <br>
                        </div>
                      </template>
                    </td>
                  </tr>
                  <tr>
                    <td class="text-gray-6 pl-0">
                      {{ $t('product.status.self') }} :
                    </td>
                    <td class="pr-0 text-dark">
                      <b-badge
                        v-if="productInfo.status"
                        variant="success"
                      >
                        {{ $t('product.status.confirmed') }}
                      </b-badge>
                      <b-badge
                        v-else
                        variant="secondary"
                      >
                        {{ $t('product.status.draft') }}
                      </b-badge>
                    </td>
                  </tr>
                  <tr>
                    <td class="text-gray-6 pl-0">
                      {{ $t('review.author') }} :
                    </td>
                    <td class="pr-0 text-dark">
                      <a-popover
                        v-if="productInfo.seller"
                        placement="rightBottom"
                      >
                        <template
                          v-if="productInfo.seller.company_info"
                          slot="title"
                        >
                          <strong>{{ $l(productInfo.seller.company_info.title) }}</strong>
                        </template>
                        <template slot="content">
                          <p>{{ productInfo.seller.email }}</p>
                          <p>{{ productInfo.seller.phone | phoneFormatter }}</p>
                        </template>
                        <a @click.prevent>{{ productInfo.seller.name }}</a>
                      </a-popover>
                      <span class="ml-2">{{ productInfo.date_created | dateFormatter("YYYY-MM-DD HH:mm") }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <a-tabs
          class="product-tab"
          type="card"
        >
          <a-tab-pane
            key="1"
            tab="Атрибуты"
          >
            <AttributesPicker
              ref="attribute_picker"
              :attributes="attributes"
              @input="postAttributes"
            />
            <b-button
              class="mt-4 text-nowrap"
              variant="success"
              @click="$refs.attribute_picker.validateAttributes()"
            >
              <i class="mr-3 fe fe-save" />{{ $t('common.save') }}
            </b-button>
          </a-tab-pane>
          <a-tab-pane
            key="2"
            tab="Описание и фото"
          >
            <Description
              ref="description_editor"
              :default-description="descriptions"
              :default-images="images"
              :is-admin="true"
              :product-id="productId"
              is-suggested
              @input="postDescription"
            />
            <b-button
              class="mt-4 text-nowrap"
              variant="success"
              @click="$refs.description_editor.validateDescription()"
            >
              <i class="mr-3 fe fe-save" />{{ $t('common.save') }}
            </b-button>
          </a-tab-pane>
          <a-tab-pane
            key="3"
            tab="Вариация и остаток"
          >
            <VariationsEditor
              ref="variation_editor"
              :default-variations="variations"
              :images="imagePool"
              :product-id="productId"
              is-suggested
            />
          </a-tab-pane>
        </a-tabs>
        <a-popconfirm
          placement="top"
          :ok-text="$t('common.confirm')"
          :cancel-text="$t('common.cancel')"
          @confirm="acceptSuggestion"
        >
          <template slot="title">
            <p>{{ $t('suggested.sure_to_accept') }}</p>
          </template>
          <a-button
            type="primary"
            block
            class="mt-3"
          >
            {{ $t('suggested.accept') }}
          </a-button>
        </a-popconfirm>
      </div>
      <a-modal
        v-if="productInfo && productInfo.title"
        v-model="showEditTitleModal"
        @cancel="showEditTitleModal = false"
        @ok="editProductTitle"
      >
        <a-form
          layout="horizontal"
          :label-col="{ span: 8 }"
          :wrapper-col="{ span: 10 }"
        >
          <a-form-item :label="$t('product.title') + ' (RU)'">
            <a-input v-model="titleForm.title.ru" />
          </a-form-item>
          <a-form-item :label="$t('product.title') + ' (UZ)'">
            <a-input v-model="titleForm.title.uz" />
          </a-form-item>
          <a-form-item :label="$t('product.ikpu')">
            <a-input
              v-model="titleForm.ikpu"
              :class="{
                errorInput: this.titleForm.ikpu === null || this.titleForm.ikpu.length !== 17,
                successInput: this.titleForm.ikpu && this.titleForm.ikpu.length === 17,
              }"
              @blur="validateIkpu"
              @change="onIkpuChange"
            />
          </a-form-item>
        </a-form>
      </a-modal>
    </div>
  </div>
</template>
<script>
import AttributesPicker from '@/components/product/add/AttributesPicker'
import VariationsEditor from '@/components/product/add/VariationsEditor'
import Description from '@/components/product/Description'

import service from '@/services/api/admin/library/suggested-product-service'
const baseMediaPath = process.env.VUE_APP_API_CDN

export default {
  name: 'ProductEdit',
  components: {
    VariationsEditor,
    Description,
    AttributesPicker,
  },
  data: function () {
    return {
      productId: this.$route.params.id,
      ikpuInvalid: true,
      productInfo: null,
      titleForm: {
        title: {},
        ikpu: '',
        code: '',
      },
      attributes: [],
      images: [],
      descriptions: null,
      imagePool: [],
      variations: [],
      showEditTitle: false,
      showEditTitleModal: false,
    }
  },
  computed: {
    maxPrice () {
      if (this.variations && this.variations.length > 0) {
        return this.variations.map(v => v.price).reduce((acc, curr) => {
          return curr > acc ? curr : acc
        })
      }
      return 0
    },
    minPrice () {
      if (this.variations && this.variations.length > 0) {
        return this.variations.map(v => v.price).reduce((acc, curr) => {
          return curr > acc ? curr : acc
        })
      }
      return 0
    },
  },
  mounted () {
    this.fetchInfo()
    this.fetchStaticAttributes()
    this.fetchDescription()
    this.fetchImages()
    this.fetchVariations()
  },
  methods: {
    fetchInfo () {
      service.getProduct(this.productId)
        .then((response) => {
          if (response && response.data && response.data.content) {
            this.productInfo = response.data.content
            this.titleForm.title = { ...this.titleForm.title, ...this.productInfo.title }
            this.titleForm.ikpu = response.data.content.ikpu
            this.titleForm.code = this.productInfo.code
          }
        })
    },
    openTitleModal() {
      this.ikpuInvalid = this.titleForm.ikpu === null || this.titleForm.ikpu.length !== 17
      this.showEditTitleModal = true
    },
    onIkpuChange (e) {
      const { value } = e.target
      const numberRegex = /^\d+$/
      if (numberRegex.test(value) && !isNaN(value)) {
        this.titleForm.ikpu = value
      } else {
        this.titleForm.ikpu = this.titleForm.ikpu.replace(/[^0-9]/g, '').replace(/(\..*)\./g, '$1')
      }
    },
    validateIkpu(e) {
      const { value } = e.target
      if (value.length !== 17) {
        this.$notification.error({ message: this.$t('common.error'), description: this.$t('product_add.messages.ikpu_required') })
        this.ikpuInvalid = true
      } else {
        this.ikpuInvalid = false
      }
    },
    fetchStaticAttributes () {
      service.getStaticAttributes(this.productId)
        .then((response) => {
          if (response && response.data && response.data.content)
            this.attributes = response.data.content.static_attributes.map((attr) => {
              return this.mapAttributeDto(attr)
            })
        })
    },
    fetchDescription () {
      service.getProductDescription(this.productId)
        .then((response) => {
          if (response && response.data && response.data.content) {
            this.descriptions = response.data.content
          }
        })
    },
    fetchImages () {
      service.getProductImages(this.productId)
        .then((response) => {
          if (response && response.data && response.data.content) {
            this.images = response.data.content.map(img => {
              if (img.full && img.full.path)
                return this.mapImageObject(img)
            })
            this.imagePool = this.images
          }
        })
    },
    fetchVariations () {
      service.getVariations(this.productId)
        .then((response) => {
          if (response && response.data && response.data.content) {
            const rawVariations = response.data.content
            rawVariations.forEach((variation) => {
              variation.editableAttributes = variation.attributes.map((attr) => {
                return this.mapAttributeDto(attr)
              })
            })
            this.variations = rawVariations
          }
        })
    },
    postAttributes (attributesMap) {
      const payload = Object.values(attributesMap)
        .map((attribute) => {
          attribute.attribute_id = parseInt(attribute.attribute_id)
          return attribute
        })

      service.putStaticAttributes(this.productId, { static_attributes: payload })
        .catch((error) => {
          if (error && error.response && error.response.data && error.response.data.result) {
            const result = error.response.data.result
            this.$notification.error({ message: this.$t('common.error'), description: this.$l(result.description) })
          }
        })
    },
    postDescription (payload) {
      this.imagePool = payload.images

      const description = {
        id: this.productId,
        description: payload.description,
        short_description: payload.short_description,
      }

      service.postProductDescription(this.productId, description)
        .then(() => {
          this.$notification.success({
            message: this.$t('product_add.messages.posting_description'),
            description: '',
          })
        })
    },
    editProductTitle () {
      if (this.ikpuInvalid) {
        this.$notification.error({ message: this.$t('common.error'), description: this.$t('product_add.messages.invalid_ikpu') })
      } else {
        service.editProductTitle(this.productId, this.titleForm).then(() => {
          this.productInfo = {
            ...this.productInfo,
            ...this.titleForm,
          }
          this.showEditTitleModal = false
        })
      }
    },
    acceptSuggestion () {
      service.acceptSuggestion(this.productId)
        .then((response) => {
          if (response.data && response.data.content && response.data.content.id) {
            this.$router.push(`/admin/library/product/${response.data.content.id}`)
          }
        })
    },
    mapAttributeDto (attr) {
      return {
        key: attr.attribute_id,
        title: attr.title,
        values: attr.available_values.map((val) => {
          return {
            key: val.attribute_value_id, title: val.value, code: val.code,
          }
        }),
        selected: attr.selected_values,
        type: attr.type,
      }
    },
    mapImageObject (image) {
      if (image.full) {
        return {
          url: image.full.path.startsWith('http') ? image.full.path : baseMediaPath + image.full.path,
          status: 'done',
          name: image.id + image.full.path.substr(image.full.path.lastIndexOf('.')),
          uid: image.id,
        }
      }
    },
  },
}
</script>
<style scoped>
 .errorInput {
    border: 1px solid red;
  }
  .successInput {
    border: 1px solid rgb(33, 207, 17);
  }
</style>
